import React, { useEffect, useRef, useState } from "react";
import { TIER_META } from "../../data/casesData";
import { useTheme } from "../../context/ThemeContext";
import { playTickSimple } from "../../lib/sounds";
import ItemImage from "./ItemImage";

const COUNT = 48;
const WIN = 40;
const TILE_D = 128;
const TILE_M = 104;
const getTile = () => (typeof window !== "undefined" && window.innerWidth < 640 ? TILE_M : TILE_D);

function buildStrip(items, winner) {
  const rarest = [...items].sort((a, b) => a.rate - b.rate).slice(0, 4);
  const arr = Array.from({ length: COUNT }, () => items[Math.floor(Math.random() * items.length)]);
  for (let i = 0; i < COUNT; i++) if (Math.random() < 0.18) arr[i] = rarest[Math.floor(Math.random() * rarest.length)];
  if (winner) {
    arr[WIN] = winner;
    arr[WIN - 1] = rarest[Math.floor(Math.random() * rarest.length)];
    arr[WIN + 1] = rarest[Math.floor(Math.random() * rarest.length)];
  }
  return arr;
}

export default function VerticalReel({ caseObj, winner, spinKey, onDone, accent, height = 268 }) {
  const { dark } = useTheme();
  const bg = dark ? "#19142A" : "#FFFFFF";
  const [strip, setStrip] = useState(() => buildStrip(caseObj.items, null));
  const [ty, setTy] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [cfg, setCfg] = useState({ dur: 3, ease: "cubic-bezier(0.10,0.80,0.22,1.01)" });
  const [tile, setTile] = useState(getTile);
  const wrapRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const r = () => setTile(getTile());
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  useEffect(() => {
    if (!spinKey) return;
    const h = wrapRef.current ? wrapRef.current.clientHeight : height;
    setStrip(buildStrip(caseObj.items, winner));
    setSpinning(false);
    setTy(0);
    let rafId, tickRaf, settleTimer, doneTimer;
    const dur = 4.6 + Math.random() * 1.1;
    const base = -(WIN * tile + tile / 2 - h / 2);
    const edge = (Math.random() * 2 - 1) * (tile * 0.34);
    const target = base + edge;
    rafId = requestAnimationFrame(() => requestAnimationFrame(() => {
      setCfg({ dur, ease: "cubic-bezier(0.10,0.80,0.22,1.01)" });
      setSpinning(true);
      setTy(target);
      let nextT = tile * 0.6;
      const readY = () => {
        const el = trackRef.current;
        if (!el) return 0;
        const t = getComputedStyle(el).transform;
        if (!t || t === "none") return 0;
        const m = t.match(/matrix.*\((.+)\)/);
        if (!m) return 0;
        const p = m[1].split(", ");
        return Math.abs(parseFloat(p[5] || p[13] || 0));
      };
      const loop = () => {
        const y = readY();
        while (y >= nextT) { playTickSimple(); nextT += tile; }
        tickRaf = requestAnimationFrame(loop);
      };
      tickRaf = requestAnimationFrame(loop);
      settleTimer = setTimeout(() => { setCfg({ dur: 0.7, ease: "cubic-bezier(0.22,1,0.36,1)" }); setTy(base); }, dur * 1000 + 70);
      doneTimer = setTimeout(() => onDone && onDone(), dur * 1000 + 70 + 780);
    }));
    return () => { cancelAnimationFrame(rafId); cancelAnimationFrame(tickRaf); clearTimeout(settleTimer); clearTimeout(doneTimer); };
    // eslint-disable-next-line
  }, [spinKey]);

  const ac = accent || caseObj.accent;
  return (
    <div ref={wrapRef} className="relative overflow-hidden" style={{ height }}>
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[3px] z-20" style={{ background: ac, boxShadow: `0 0 16px 2px ${ac}` }} />
      <div className="absolute top-1/2 -translate-y-1/2 left-0 z-20 text-sm" style={{ color: ac }}>▶</div>
      <div className="absolute top-1/2 -translate-y-1/2 right-0 z-20 text-sm" style={{ color: ac }}>◀</div>
      <div className="absolute inset-x-0 top-0 h-12 z-10 pointer-events-none" style={{ background: `linear-gradient(180deg, ${bg}, transparent)` }} />
      <div className="absolute inset-x-0 bottom-0 h-12 z-10 pointer-events-none" style={{ background: `linear-gradient(0deg, ${bg}, transparent)` }} />
      <div ref={trackRef} className="flex flex-col items-center"
        style={{ transform: `translateY(${ty}px)`, transition: spinning ? `transform ${cfg.dur}s ${cfg.ease}` : "none", willChange: "transform" }}>
        {strip.map((it, i) => {
          const color = TIER_META[it.tier].color;
          return (
            <div key={i} style={{ height: tile }} className="shrink-0 w-full flex flex-col items-center justify-center gap-1">
              <div className="relative h-16 w-16 flex items-center justify-center">
                <span className="absolute inset-1 rounded-full blur-xl opacity-70" style={{ background: `radial-gradient(circle, ${color} 0%, transparent 70%)` }} />
                <div className="relative" style={{ filter: `drop-shadow(0 4px 10px ${color}cc)` }}>
                  <ItemImage item={it} imgClass="h-14 w-14" iconClass="h-8 w-8" color={color} eager />
                </div>
              </div>
              <span className={`text-[10px] font-semibold text-center leading-tight line-clamp-1 max-w-[120px] px-1 ${dark ? "text-white/85" : "text-white"}`}>{it.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
